import axios from 'axios';
import {
  ServiceCategoryId,
  ServiceListing,
  ServicePriceRangeId,
  ServiceSortId,
} from '../pages/main/services/constants';
import {API_BASE_URL} from './authApi';

export interface ServicesResponse {
  success: boolean;
  services: ServiceListing[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface GetServicesParams {
  keyword?: string;
  category?: ServiceCategoryId;
  priceRange?: ServicePriceRangeId;
  sort?: ServiceSortId;
  page?: number;
  limit?: number;
}

type ServicesErrorResponse = {
  message?: string;
  error?: string | {message?: string};
};

export class ServicesApiError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'ServicesApiError';
    this.status = status;
  }
}

const servicesClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (axios.isAxiosError(error)) {
    const errorData = error.response?.data as ServicesErrorResponse | undefined;
    const nestedError =
      typeof errorData?.error === 'string'
        ? errorData.error
        : errorData?.error?.message;

    return errorData?.message || nestedError || error.message || fallbackMessage;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallbackMessage;
};

export const servicesApi = {
  async getServices(
    token: string,
    params: GetServicesParams = {},
    signal?: AbortSignal
  ): Promise<ServicesResponse> {
    try {
      const response = await servicesClient.get<ServicesResponse>('/services', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          q: params.keyword,
          category: params.category,
          priceRange: params.priceRange,
          sort: params.sort,
          page: params.page,
          limit: params.limit,
        },
        signal,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') {
        throw error;
      }

      throw new ServicesApiError(
        getErrorMessage(error, 'Failed to load services'),
        axios.isAxiosError(error) ? error.response?.status : undefined
      );
    }
  },
};
